// Lazily loaded popup of `Menu` (Base UI). Open state, trigger and layer live in Menu.tsx; the item
// renderer is shared with ContextMenu.
import { Menu as BaseMenu } from '@base-ui/react/menu';
import type { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu';
import { Fragment, type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { Icon } from '../icons';
import { formatShortcut, useUiLocale } from '../i18n';
import { SheetGrabber, sideAlign } from '../layers/base';
import type { LayerCloseReason } from '../layers/types';
import { type LayerHandle, LayerParent } from '../layers/useLayer';
import { cx } from './cx';
import type { MenuAction, MenuItem, MenuProps } from './Menu';
import { dismissWithTrigger, useFocusReturnGuard } from './PopoverPopup';

function Shortcut({ combo }: { combo: string }) {
  const locale = useUiLocale();
  return <span className="menu-shortcut">{formatShortcut(combo, { locale })}</span>;
}

/** Empty icon slot: rows without an icon line up with their siblings that have one. */
const IconSlot = () => <span className="menu-icon-slot" aria-hidden="true" />;

/** True when some row of this menu level carries an icon (then every row reserves the slot). */
function hasIcons(items: MenuItem[]): boolean {
  return items.some((i) => {
    if (i.type === 'submenu') return !!i.icon;
    if (i.type === 'separator' || i.type === 'label' || i.type === 'radio') return false;
    return !!i.icon || !!i.leading;
  });
}

function ItemContent({ item, slot }: { item: MenuAction; slot: boolean }) {
  return (
    <>
      {item.leading ?? (item.icon ? <Icon name={item.icon} size={15} /> : slot ? <IconSlot /> : null)}
      {item.description ? (
        <span className="menu-text">
          <span className="menu-text-label" title={item.label}>
            {item.label}
          </span>
          <span className="menu-desc">{item.description}</span>
        </span>
      ) : (
        <span className="menu-text menu-text-solo" title={item.label}>
          {item.label}
        </span>
      )}
      {item.meta ? <span className="menu-meta">{item.meta}</span> : null}
      {item.shortcut ? <Shortcut combo={item.shortcut} /> : null}
    </>
  );
}

type Parts = typeof BaseMenu | typeof BaseContextMenu;

/** Render `items` (shared by Menu, ContextMenu and submenus). */
export function MenuItems({
  items,
  parts,
  layer,
  compact,
}: {
  items: MenuItem[];
  parts: Parts;
  layer: LayerHandle;
  compact: boolean;
}) {
  const P = parts as typeof BaseMenu;
  const slot = hasIcons(items);
  // Group the items following a label so screen readers announce sections.
  const blocks: Array<{ label?: string; items: MenuItem[] }> = [{ items: [] }];
  for (const item of items) {
    if (item.type === 'label') blocks.push({ label: item.label, items: [] });
    else if (item.type === 'separator') {
      blocks[blocks.length - 1]!.items.push(item);
      if (blocks[blocks.length - 1]!.label) blocks.push({ items: [] });
    } else blocks[blocks.length - 1]!.items.push(item);
  }
  const renderItem = (item: MenuItem, i: number): ReactNode => {
    switch (item.type) {
      case 'separator':
        return <P.Separator key={`sep-${i}`} className="menu-sep" />;
      case 'label':
        return null;
      case 'radio':
        return (
          <P.RadioGroup
            key={`radio-${i}`}
            value={item.value}
            onValueChange={(v) => item.onValueChange(String(v))}
            className="menu-group"
          >
            {item.label ? <P.GroupLabel className="menu-label sec">{item.label}</P.GroupLabel> : null}
            {item.options.map((o) => (
              <P.RadioItem
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                closeOnClick={!item.keepOpen}
                className="menu-item"
                label={o.label}
              >
                {o.icon ? <Icon name={o.icon} size={15} /> : null}
                <span className="menu-text">{o.label}</span>
                <P.RadioItemIndicator className="menu-check" keepMounted={false}>
                  <Icon name="check" size={14} />
                </P.RadioItemIndicator>
              </P.RadioItem>
            ))}
          </P.RadioGroup>
        );
      case 'submenu':
        return (
          <P.SubmenuRoot key={`sub-${i}`}>
            <P.SubmenuTrigger className="menu-item" disabled={item.disabled} label={item.label}>
              {item.icon ? <Icon name={item.icon} size={15} /> : slot ? <IconSlot /> : null}
              <span className="menu-text">{item.label}</span>
              <Icon name="chevron-right" size={14} className="menu-sub-chevron" />
            </P.SubmenuTrigger>
            <P.Portal container={layer.container} className="dr-layer" {...layer.portalProps}>
              <P.Positioner
                className="dr-positioner"
                sideOffset={compact ? 0 : -4}
                alignOffset={-4}
                collisionPadding={8}
                data-sheet={compact || undefined}
              >
                <P.Popup className="popover menu" data-sheet={compact || undefined} aria-label={item.label}>
                  <MenuItems items={item.items} parts={parts} layer={layer} compact={compact} />
                </P.Popup>
              </P.Positioner>
            </P.Portal>
          </P.SubmenuRoot>
        );
      default: {
        const action = item;
        if (action.checked !== undefined)
          return (
            <P.CheckboxItem
              key={`${action.label}-${i}`}
              checked={action.checked}
              onCheckedChange={() => action.onSelect()}
              closeOnClick={!action.keepOpen}
              disabled={action.disabled}
              label={action.label}
              className={cx('menu-item', action.danger && 'danger')}
            >
              <ItemContent item={action} slot={slot} />
              <P.CheckboxItemIndicator className="menu-check" keepMounted={false}>
                <Icon name="check" size={14} />
              </P.CheckboxItemIndicator>
            </P.CheckboxItem>
          );
        return (
          <P.Item
            key={`${action.label}-${i}`}
            onClick={() => action.onSelect()}
            closeOnClick={!action.keepOpen}
            disabled={action.disabled}
            label={action.label}
            className={cx('menu-item', action.danger && 'danger')}
          >
            <ItemContent item={action} slot={slot} />
          </P.Item>
        );
      }
    }
  };
  return (
    <>
      {blocks.map((block, b) =>
        block.label ? (
          <P.Group key={`g-${b}`} className="menu-group">
            <P.GroupLabel className="menu-label sec">{block.label}</P.GroupLabel>
            {block.items.map(renderItem)}
          </P.Group>
        ) : (
          <Fragment key={`g-${b}`}>{block.items.map(renderItem)}</Fragment>
        ),
      )}
    </>
  );
}

export default function MenuPopup({
  open,
  popupId,
  anchor,
  layer,
  compact,
  items,
  placement = 'bottom-start',
  label,
  width,
  header,
  focusOnOpen,
  onRequestOpenChange,
  onExited,
}: Pick<MenuProps, 'items' | 'placement' | 'label' | 'width' | 'header'> & {
  open: boolean;
  popupId: string;
  anchor: HTMLElement;
  layer: LayerHandle;
  compact: boolean;
  focusOnOpen: RefObject<'first' | 'last' | null>;
  onRequestOpenChange: (open: boolean, reason?: LayerCloseReason) => void;
  onExited: () => void;
}) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const mayReturnFocus = useFocusReturnGuard(open, popupRef, anchor);
  const { side, align } = sideAlign(placement);
  // Keyboard opening lands on the first (or last) item, as in every native menu.
  useEffect(() => {
    if (!open) return;
    // The request is consumed once a row holds focus (not on read: StrictMode runs effects twice).
    const which = focusOnOpen.current;
    if (!which) return;
    // Base UI moves focus to the popup once it is positioned; keep trying for a few frames until the
    // row holds focus (a lazily loaded popup mounts a frame later than an already loaded one).
    let frame = 0;
    let tries = 0;
    const focusRow = () => {
      const rows = popupRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([data-disabled]), [role="menuitemcheckbox"]:not([data-disabled]), [role="menuitemradio"]:not([data-disabled])',
      );
      const row = rows && (which === 'first' ? rows[0] : rows[rows.length - 1]);
      if (row && document.activeElement !== row) row.focus();
      if (row && document.activeElement === row) focusOnOpen.current = null;
      if (++tries < 6 && (!row || document.activeElement !== row || tries < 3))
        frame = requestAnimationFrame(focusRow);
    };
    frame = requestAnimationFrame(focusRow);
    return () => cancelAnimationFrame(frame);
  }, [open, focusOnOpen]);
  return (
    <BaseMenu.Root
      open={open}
      modal={false}
      onOpenChange={(next, details) => {
        if (next) return onRequestOpenChange(true);
        const reason = dismissWithTrigger(layer, anchor, details);
        if (reason) onRequestOpenChange(false, reason);
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onExited();
      }}
    >
      {/* The menu opens from our own trigger (Menu.tsx), so Base UI never sees one. Its submenus find
          their parent through the trigger-owned floating node, so a hidden trigger claims that role
          while the menu is open — without it, opening a submenu closes the whole menu. */}
      <BaseMenu.Trigger nativeButton={false} render={<span hidden aria-hidden="true" />} tabIndex={-1} />
      <BaseMenu.Portal container={layer.container} className="dr-layer" {...layer.portalProps}>
        {compact ? <BaseMenu.Backdrop className="scrim sheet-scrim" /> : null}
        <BaseMenu.Positioner
          anchor={anchor}
          className="dr-positioner"
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          data-sheet={compact || undefined}
        >
          <BaseMenu.Popup
            ref={popupRef}
            id={popupId}
            className="popover menu"
            aria-label={label}
            data-sheet={compact || undefined}
            style={width ? { width } : undefined}
            finalFocus={() => (layer.focusMovedAway() || !mayReturnFocus() ? false : anchor)}
          >
            {compact ? <SheetGrabber onDismiss={() => void layer.requestClose('swipe')} /> : null}
            <LayerParent id={layer.id}>
              {header}
              <MenuItems items={items} parts={BaseMenu} layer={layer} compact={compact} />
            </LayerParent>
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}
